/**
 * bugHunt · «найди баг» mini-hunt (S01 MS-02 · S02 REQ-04/09/13 · S03 DOC-S3-05).
 *
 * Two modes (per-hunt via `data-mode`):
 *
 * SIMPLE (default · S01 MS-02 «первый баг» — щадящий FTUE-вход): tapping a bug
 * zone reveals its finding card; tapping a clean zone rewards the check.
 *
 * VERDICT (`data-mode="verdict"` · S02/S03): tapping a zone opens a generated
 * verdict row — «Это баг / Всё ок». Every zone demands a decision; wrong calls
 * have named, pedagogical costs: a missed bug («уехал бы в прод») or a false
 * alarm («разработчик потратил бы время на небаг»). The final reveals when ALL
 * zones are decided; on imperfect runs the optional authored `.bh-fin-miss`
 * is shown instead of `.bh-fin`, prefixed with an engine-built score tag.
 *
 * Markup contract (authored · no-JS degradable — finding cards visible as list):
 *   <div class="bughunt [bh-doc] [bh-blk|bh-def]" data-total="3" [data-mode="verdict"]>
 *     <div class="bh-bar">
 *       <div class="bh-task">…</div>
 *       <span class="bh-count">…<span class="bh-vnum">0</span>/5 · …<span class="bh-num">0</span>/3</span>
 *     </div>
 *     <div class="bh-phone">…</div> | <div class="bh-docu">…</div>
 *       <button type="button" class="bh-zone" data-z="x" data-kind="bug">…</button>
 *       <button type="button" class="bh-zone" data-kind="clean" data-note="…">…</button>
 *     <div class="bh-feed">
 *       <button type="button" class="bh-hintbtn">…</button> + <div class="bh-hint">…</div>
 *       <div class="bh-card src" data-for="x">…</div> × bugs
 *       <div class="bh-fin">…success payoff…</div>
 *       <div class="bh-fin-miss">…imperfect payoff (verdict mode)…</div>
 *     </div>
 *   </div>
 * `.bh-vnum` (verdict counter) и `.bh-fin-miss` нужны только verdict-режиму.
 *
 * StrictMode-safe: authored DOM never destroyed; cleanup removes listeners,
 * generated rows/cards, state classes and dataset flags, restores tag texts.
 */

const ALARM_NOTE =
  ' <em>Записал(а) бы это в отчёт — разработчик потратил бы время на «воспроизведение» небага. Перепроверяй перед записью.</em>';

export function initBugHunt(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const hunts = Array.from(root.querySelectorAll<HTMLElement>('.bughunt'));
  for (const hunt of hunts) {
    if (hunt.dataset.bhInit === '1') continue;
    hunt.dataset.bhInit = '1';
    hunt.classList.add('bh-ready');

    const verdictMode = hunt.dataset.mode === 'verdict';
    const zones = Array.from(hunt.querySelectorAll<HTMLButtonElement>('.bh-zone'));
    const feed = hunt.querySelector<HTMLElement>('.bh-feed');
    const numEl = hunt.querySelector<HTMLElement>('.bh-num');
    const vnumEl = hunt.querySelector<HTMLElement>('.bh-vnum');
    const fin = hunt.querySelector<HTMLElement>('.bh-fin');
    const finMiss = hunt.querySelector<HTMLElement>('.bh-fin-miss');
    const hintBtn = hunt.querySelector<HTMLButtonElement>('.bh-hintbtn');
    const hint = hunt.querySelector<HTMLElement>('.bh-hint');
    const total = Number(hunt.dataset.total ?? zones.filter((z) => z.dataset.kind === 'bug').length);
    if (!feed || zones.length === 0) continue;

    // Finding number = the bug card's AUTHORED position, not tap order. The cards
    // sit in fixed DOM slots, so numbering by discovery order made the labels jump
    // (bug 13 AUG: cards showed «находка №4, №2, №3, №1»). Progress count (.bh-num)
    // still tracks how many were found.
    const srcCards = Array.from(feed.querySelectorAll<HTMLElement>('.bh-card.src'));
    const findingNo = (zid: string): number => {
      const i = srcCards.findIndex((c) => c.dataset.for === zid);
      return i >= 0 ? i + 1 : found + 1;
    };

    let found = 0;
    let taps = 0;
    let decided = 0;
    let missed = 0;
    let alarms = 0;
    const generated: HTMLElement[] = [];
    const touchedTags: Array<[HTMLElement, string]> = [];
    const handlers: Array<[HTMLElement, string, EventListener]> = [];

    const on = (el: HTMLElement, ev: string, fn: EventListener) => {
      el.addEventListener(ev, fn);
      handlers.push([el, ev, fn]);
    };

    const insertCard = (el: HTMLElement) => {
      generated.push(el);
      const anchor = fin ?? finMiss;
      if (anchor) feed.insertBefore(el, anchor);
      else feed.appendChild(el);
    };

    const cleanCard = (note: string, tag: string, cls: string) => {
      const card = document.createElement('div');
      card.className = `bh-card ${cls}`;
      const t = document.createElement('div');
      t.className = 'tag';
      t.textContent = tag;
      const p = document.createElement('p');
      p.innerHTML = note;
      card.append(t, p);
      insertCard(card);
    };

    const revealBugCard = (z: HTMLElement, tagText: string, miss: boolean) => {
      const card = hunt.querySelector<HTMLElement>(`.bh-card.src[data-for='${z.dataset.z ?? ''}']`);
      if (!card) return;
      card.classList.add('live');
      if (miss) card.classList.add('miss');
      const tag = card.querySelector<HTMLElement>('.tag');
      if (tag) {
        touchedTags.push([tag, tag.textContent ?? '']);
        tag.textContent = tagText;
      }
    };

    const maybeHint = () => {
      if (taps >= 2 && found < total) hintBtn?.classList.add('show');
    };

    const hideHint = () => {
      hintBtn?.classList.remove('show');
      hint?.classList.remove('show');
    };

    const finishVerdict = () => {
      hideHint();
      if (missed === 0 && alarms === 0) {
        fin?.classList.add('on');
      } else if (finMiss) {
        const stats = document.createElement('div');
        stats.className = 'tag';
        stats.textContent = `вердиктов верных ${zones.length - missed - alarms} из ${zones.length}`;
        finMiss.insertBefore(stats, finMiss.firstChild);
        generated.push(stats);
        finMiss.classList.add('on');
      } else {
        fin?.classList.add('on');
      }
    };

    if (hintBtn) {
      on(hintBtn, 'click', () => {
        hint?.classList.add('show');
        hintBtn.classList.remove('show');
      });
    }

    for (const z of zones) {
      if (!verdictMode) {
        on(z, 'click', () => {
          if (z.classList.contains('found') || z.classList.contains('checked')) return;
          taps++;
          if (z.dataset.kind === 'bug') {
            z.classList.add('found');
            found++;
            if (numEl) numEl.textContent = String(found);
            revealBugCard(z, `находка №${findingNo(z.dataset.z ?? '')}`, false);
            if (found >= total) {
              fin?.classList.add('on');
              hideHint();
            }
          } else {
            z.classList.add('checked');
            cleanCard(z.dataset.note ?? 'Тут всё в порядке — и убедиться в этом тоже было работой.', 'проверено · работает', 'clean');
          }
          maybeHint();
        });
        continue;
      }

      // verdict mode: generated verdict row after the zone
      const row = document.createElement('div');
      row.className = 'bh-verdict';
      row.innerHTML =
        '<span class="vq">Твой вердикт:</span>' +
        '<button type="button" class="bh-vbtn" data-v="bug">Это баг</button>' +
        '<button type="button" class="bh-vbtn" data-v="ok">Всё ок</button>';
      z.insertAdjacentElement('afterend', row);
      generated.push(row);

      on(z, 'click', () => {
        if (z.dataset.bhDone) return;
        taps++;
        hunt.querySelectorAll('.bh-verdict.open').forEach((x) => x.classList.remove('open'));
        row.classList.add('open');
        maybeHint();
      });

      row.querySelectorAll<HTMLButtonElement>('.bh-vbtn').forEach((b) => {
        on(b, 'click', () => {
          if (z.dataset.bhDone) return;
          z.dataset.bhDone = '1';
          row.classList.remove('open');
          decided++;
          if (vnumEl) vnumEl.textContent = String(decided);
          const isBug = z.dataset.kind === 'bug';
          const saidBug = b.dataset.v === 'bug';
          if (isBug && saidBug) {
            z.classList.add('found');
            found++;
            if (numEl) numEl.textContent = String(found);
            revealBugCard(z, `находка №${findingNo(z.dataset.z ?? '')} · вердикт верный`, false);
          } else if (isBug && !saidBug) {
            z.classList.add('missed');
            missed++;
            revealBugCard(z, 'пропущено · ты сказал(а) «всё ок»', true);
          } else if (!isBug && !saidBug) {
            z.classList.add('checked');
            cleanCard(z.dataset.note ?? 'Тут всё в порядке.', 'вердикт верный · работает', 'clean');
          } else {
            z.classList.add('falsealarm');
            alarms++;
            cleanCard((z.dataset.note ?? 'Тут всё в порядке.') + ALARM_NOTE, 'ложная тревога', 'alarm');
          }
          if (decided === zones.length) finishVerdict();
        });
      });
    }

    cleanups.push(() => {
      handlers.forEach(([el, ev, fn]) => el.removeEventListener(ev, fn));
      generated.forEach((el) => el.remove());
      touchedTags.forEach(([tag, txt]) => {
        tag.textContent = txt;
      });
      zones.forEach((z) => {
        z.classList.remove('found', 'checked', 'missed', 'falsealarm');
        delete z.dataset.bhDone;
      });
      hunt.querySelectorAll('.bh-card.src.live').forEach((c) => c.classList.remove('live', 'miss'));
      fin?.classList.remove('on');
      finMiss?.classList.remove('on');
      hint?.classList.remove('show');
      hintBtn?.classList.remove('show');
      if (numEl) numEl.textContent = '0';
      if (vnumEl) vnumEl.textContent = '0';
      hunt.classList.remove('bh-ready');
      delete hunt.dataset.bhInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
